import { useEffect, useState } from 'react';
import type { DeadlinePublic } from '@momentum/shared';

export interface DeadlineStatus {
  /** Milliseconds remaining, or `null` when no deadline with this id is armed. Never negative. */
  remainingMs: number | null;
  /** True while the session is paused and this deadline's remaining time is frozen. */
  paused: boolean;
}

/**
 * Shared countdown hook for every renderer (board, poc-choice, fixture-team-2v2). `deadlines` is
 * `InstanceView.deadlines`; `id` is the module-chosen deadline id (`think`, `discuss`, `incident`, …).
 * A `running` deadline ticks every 250ms against `serverTime = Date.now() + serverTimeOffset` so the
 * countdown reads correctly regardless of client clock skew. A `paused` deadline reports its frozen
 * `remainingMs` without ticking.
 */
export function useDeadline(deadlines: DeadlinePublic[], id: string, serverTimeOffset: number): DeadlineStatus {
  const deadline = deadlines.find((d) => d.id === id) ?? null;
  const runningAt = deadline && deadline.mode === 'running' ? deadline.at : null;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (runningAt === null) return;
    const interval = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(interval);
  }, [runningAt]);

  if (!deadline) return { remainingMs: null, paused: false };
  if (deadline.mode === 'paused') {
    return { remainingMs: Math.max(deadline.remainingMs, 0), paused: true };
  }
  const remainingMs = Math.max(deadline.at - (now + serverTimeOffset), 0);
  return { remainingMs, paused: false };
}
